import { useEffect, useMemo, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Eye, EyeOff, Loader2 } from "lucide-react";
import { toast } from "sonner";

import { BrandLogo } from "@/components/BrandLogo";
import { ThemeToggle } from "@/components/ThemeToggle";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import { cn } from "@/lib/utils";
import { evaluatePasswordStrength } from "@/lib/password-strength";
import { seoHead } from "@/lib/seo";

export const Route = createFileRoute("/reset-password")({
  head: () =>
    seoHead({
      title: "パスワードを再設定 | KAGEM",
      description: "KAGEMアカウントの新しいパスワードを設定します。",
      path: "/reset-password",
      noindex: true,
    }),
  component: ResetPasswordPage,
});

function ResetPasswordPage() {
  const navigate = useNavigate();

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [confirmError, setConfirmError] = useState<string | null>(null);
  const [sessionReady, setSessionReady] = useState<"checking" | "ready" | "invalid">("checking");

  const strength = useMemo(() => evaluatePasswordStrength(password), [password]);

  useEffect(() => {
    // Supabase recovery links set the session via URL hash automatically
    // when detectSessionInUrl is on (default). We listen for the
    // PASSWORD_RECOVERY event and also check current session as fallback.
    let resolved = false;

    const { data: sub } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === "PASSWORD_RECOVERY" || (event === "SIGNED_IN" && session)) {
        resolved = true;
        setSessionReady("ready");
      }
    });

    supabase.auth.getSession().then(({ data }) => {
      if (resolved) return;
      setSessionReady(data.session ? "ready" : "invalid");
    });

    return () => {
      sub.subscription.unsubscribe();
    };
  }, []);

  const validate = () => {
    let ok = true;
    if (password.length < 8) {
      setPasswordError("パスワードは8文字以上で入力してください");
      ok = false;
    } else if (strength.level === "weak") {
      setPasswordError("パスワードが弱すぎます。英大文字・小文字・数字・記号を組み合わせてください");
      ok = false;
    } else {
      setPasswordError(null);
    }
    if (password !== confirmPassword) {
      setConfirmError("パスワードが一致しません");
      ok = false;
    } else {
      setConfirmError(null);
    }
    return ok;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    setSubmitting(true);
    try {
      const { error } = await supabase.auth.updateUser({ password });
      if (error) {
        const msg = error.message?.toLowerCase() ?? "";
        if (msg.includes("same") || msg.includes("different")) {
          toast.error("以前と異なるパスワードを設定してください");
        } else if (msg.includes("session") || msg.includes("expired") || msg.includes("token")) {
          toast.error("リンクの有効期限が切れています。再度リセットメールを送信してください");
        } else if (msg.includes("network") || msg.includes("fetch")) {
          toast.error("ネットワークエラーが発生しました。接続をご確認ください");
        } else {
          toast.error(`更新に失敗しました: ${error.message}`);
        }
        setSubmitting(false);
        return;
      }

      // Sign out so the user must log in with the new password
      await supabase.auth.signOut();
      toast.success("パスワードを更新しました");
      navigate({ to: "/login" as never });
    } catch {
      toast.error("ネットワークエラーが発生しました。接続をご確認ください");
      setSubmitting(false);
    }
  };

  return (
    <main className="bg-gradient-hero relative flex min-h-screen w-full items-center justify-center overflow-hidden px-4 py-10 sm:px-6">
      <div className="absolute right-4 top-4 sm:right-6 sm:top-6">
        <ThemeToggle />
      </div>

      <div className="w-full max-w-md">
        <div className="mb-6 flex justify-center">
          <BrandLogo size="md" />
        </div>

        <Card className="border-border/60 shadow-2xl shadow-brand-primary/10 backdrop-blur-md">
          <CardHeader className="text-center">
            <CardTitle className="text-2xl">パスワードを再設定</CardTitle>
            <CardDescription>新しいパスワードを入力してください</CardDescription>
          </CardHeader>
          <CardContent>
            {sessionReady === "checking" ? (
              <div className="flex items-center justify-center py-8 text-muted-foreground">
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                <span className="text-sm">確認中...</span>
              </div>
            ) : sessionReady === "invalid" ? (
              <div className="space-y-4 text-center">
                <p className="text-sm text-destructive">
                  リセットリンクが無効か、有効期限が切れています。
                </p>
                <Button asChild variant="outline" className="w-full">
                  <a href="/forgot-password">リセットメールを再送信</a>
                </Button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4" noValidate>
                <div className="space-y-2">
                  <Label htmlFor="password">新しいパスワード</Label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      autoComplete="new-password"
                      placeholder="8文字以上"
                      value={password}
                      onChange={(e) => {
                        setPassword(e.target.value);
                        if (passwordError) setPasswordError(null);
                      }}
                      aria-invalid={!!passwordError}
                      minLength={8}
                      required
                      className="pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((s) => !s)}
                      aria-label={showPassword ? "パスワードを非表示" : "パスワードを表示"}
                      className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-muted-foreground hover:text-foreground"
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>

                  {password.length > 0 && (
                    <div className="space-y-1.5">
                      <div className="flex gap-1.5">
                        {[1, 2, 3].map((i) => (
                          <div
                            key={i}
                            className={cn(
                              "h-1.5 flex-1 rounded-full transition-colors",
                              i <= strength.score
                                ? strength.level === "weak"
                                  ? "bg-destructive"
                                  : strength.level === "medium"
                                    ? "bg-amber-500"
                                    : "bg-emerald-500"
                                : "bg-muted",
                            )}
                          />
                        ))}
                      </div>
                      <p
                        className={cn(
                          "text-xs",
                          strength.level === "weak" && "text-destructive",
                          strength.level === "medium" && "text-amber-600 dark:text-amber-500",
                          strength.level === "strong" && "text-emerald-600 dark:text-emerald-500",
                        )}
                      >
                        パスワード強度: {strength.label}
                      </p>
                    </div>
                  )}
                  {passwordError && <p className="text-xs text-destructive">{passwordError}</p>}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirm-password">新しいパスワード（確認）</Label>
                  <Input
                    id="confirm-password"
                    type={showPassword ? "text" : "password"}
                    autoComplete="new-password"
                    placeholder="もう一度入力"
                    value={confirmPassword}
                    onChange={(e) => {
                      setConfirmPassword(e.target.value);
                      if (confirmError) setConfirmError(null);
                    }}
                    aria-invalid={!!confirmError}
                    minLength={8}
                    required
                  />
                  {confirmError && <p className="text-xs text-destructive">{confirmError}</p>}
                </div>

                <Button
                  type="submit"
                  className="h-11 w-full bg-gradient-brand text-white shadow-lg shadow-brand-primary/30 hover:opacity-95"
                  disabled={submitting || password.length === 0 || confirmPassword.length === 0}
                >
                  {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                  パスワードを更新
                </Button>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </main>
  );
}
